import { CLIENT_SESSION_VERSION, normalizeHostRuntimeSnapshot, isHighEntropyToken, type HostRuntimeSnapshot } from '@hanamikoji/p2p';
import type { PlayerId } from '@hanamikoji/shared';

export { CLIENT_SESSION_VERSION };

const HOST_KEY = 'hanamikoji_offline_host_snapshot_v1';
const CLIENT_KEY = 'hanamikoji_offline_client_session_v1';
export const OFFLINE_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

export type OfflineP2PRole = 'host' | 'player';

export interface OfflineClientSessionSnapshot {
  version: 1;
  role: OfflineP2PRole;
  roomId: string;
  playerId: PlayerId;
  reconnectToken: string;
  lastStateVersion: number;
  lastViewHash: string | null;
  updatedAt: number;
}

export interface StoredHostSnapshot {
  version: 1;
  snapshot: HostRuntimeSnapshot;
  updatedAt: number;
}

export interface OfflineSessionStatus {
  exists: boolean;
  updatedAt: number | null;
  ageMs: number | null;
  isStale: boolean;
}

export function saveHostSnapshot(snapshot: HostRuntimeSnapshot): void {
  safeSet(HOST_KEY, { version: CLIENT_SESSION_VERSION, snapshot, updatedAt: Date.now() });
}

export function loadHostSnapshot(now = Date.now()): HostRuntimeSnapshot | null {
  const stored = safeGet<StoredHostSnapshot>(HOST_KEY);
  if (!stored || stored.version !== CLIENT_SESSION_VERSION || !isValidUpdatedAt(stored.updatedAt) || !stored.snapshot) return null;
  if (isOfflineSessionStale(stored.updatedAt, now)) return null;
  return normalizeHostRuntimeSnapshot(stored.snapshot);
}

export function clearHostSnapshot(): void {
  safeRemove(HOST_KEY);
}

export function saveClientSession(session: OfflineClientSessionSnapshot): void {
  safeSet(CLIENT_KEY, { ...session, version: CLIENT_SESSION_VERSION, updatedAt: Date.now() });
}

export function loadClientSession(now = Date.now()): OfflineClientSessionSnapshot | null {
  const stored = safeGet<OfflineClientSessionSnapshot>(CLIENT_KEY);
  if (!stored) return null;
  if (stored.version !== CLIENT_SESSION_VERSION) return null;
  if (!isValidUpdatedAt(stored.updatedAt) || isOfflineSessionStale(stored.updatedAt, now)) return null;
  if (stored.role !== 'host' && stored.role !== 'player') return null;
  if (!isNonEmptyString(stored.roomId) || !isPlayerId(stored.playerId) || !isHighEntropyToken(stored.reconnectToken)) return null;
  if (!Number.isSafeInteger(stored.lastStateVersion) || stored.lastStateVersion < -1) return null;
  if (stored.lastViewHash !== null && stored.lastViewHash !== undefined && !isNonEmptyString(stored.lastViewHash)) return null;
  return { ...stored, lastViewHash: stored.lastViewHash ?? null };
}

export function getRestoredOfflineClientSession(): OfflineClientSessionSnapshot | null {
  return loadClientSession();
}

export function clearClientSession(): void {
  safeRemove(CLIENT_KEY);
}

export function getOfflineSessionAge(updatedAt: number, now = Date.now()): number {
  if (!isValidUpdatedAt(updatedAt)) return Number.POSITIVE_INFINITY;
  return Math.max(0, now - updatedAt);
}

export function isOfflineSessionStale(updatedAt: number, now = Date.now(), ttlMs = OFFLINE_SESSION_TTL_MS): boolean {
  return getOfflineSessionAge(updatedAt, now) > ttlMs;
}

export function getHostSnapshotStatus(now = Date.now()): OfflineSessionStatus {
  const stored = safeGet<StoredHostSnapshot>(HOST_KEY);
  return buildSessionStatus(stored?.updatedAt, now);
}

export function getClientSessionStatus(now = Date.now()): OfflineSessionStatus {
  const stored = safeGet<OfflineClientSessionSnapshot>(CLIENT_KEY);
  return buildSessionStatus(stored?.updatedAt, now);
}

function buildSessionStatus(updatedAt: unknown, now: number): OfflineSessionStatus {
  if (!isValidUpdatedAt(updatedAt)) {
    return { exists: false, updatedAt: null, ageMs: null, isStale: false };
  }
  const ageMs = getOfflineSessionAge(updatedAt, now);
  return {
    exists: true,
    updatedAt,
    ageMs,
    isStale: ageMs > OFFLINE_SESSION_TTL_MS,
  };
}

function safeSet(key: string, value: unknown): void {
  try {
    getOfflineStorage().setItem(key, JSON.stringify(value));
  } catch {
    // Offline recovery is best effort; gameplay can continue in memory.
  }
}

function safeGet<T>(key: string): T | null {
  try {
    const raw = getOfflineStorage().getItem(key);
    return raw ? JSON.parse(raw) as T : null;
  } catch {
    return null;
  }
}

function safeRemove(key: string): void {
  try {
    getOfflineStorage().removeItem(key);
  } catch {
    // Ignore unavailable storage.
  }
}

function getOfflineStorage(): Storage {
  return import.meta.env?.VITE_P2P_TEST_STORAGE === 'session' ? sessionStorage : localStorage;
}

function isPlayerId(value: unknown): value is PlayerId {
  return value === 'p1' || value === 'p2';
}

function isValidUpdatedAt(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}
